/**
 * Maps an orchestration-endpoint failure to partner-friendly copy. Avoids
 * leaking the backend's generic "share this code with Support" wrapper or
 * internal provider errors, while surfacing the specific reason when it's safe.
 *
 * Note the error arriving here is the client-side apiClientError: the backend's
 * 400 text survives on `error.message` (its SR_API_ERROR_ACTION_PAYLOAD_VALIDATION_FAILED
 * name makes handleUserErrorMessages pass it through), while a web-layer failure
 * gets rewritten to the "share this code" wrapper, which we filter out.
 *
 * @param {Object}  error             The error passed to the requestUtilityApi onError callback.
 * @param {boolean} [args.exclusive]  Whether the failed submission had the exclusive toggle on.
 * @return {string} Partner-facing error copy.
 */
export const getEnablementErrorMessage = (error, { exclusive } = {}) => {
  const status = error?.statusCode;
  if (status === 409) {
    // The backend returns 409 for two distinct conflicts. Only the exclusive-blocked
    // case is about the toggle, so scope that copy to exclusive submissions; a
    // non-exclusive 409 means the partner is already set up.
    if (exclusive) {
      return 'This partner already has SimpleCover set up, so "Only show this provider to renters" cannot be turned on. Contact support to switch to a provider-only setup.';
    }
    return 'Tenant liability protection is already set up for this partner. Refresh the page to see the current setup, or contact support if something looks off.';
  }
  if (status === 400) {
    const specific = error?.message;
    if (specific && !/share this code/i.test(specific)) {
      return specific;
    }
    return 'Please double-check the provider details and try again.';
  }
  return 'Something went wrong while enabling tenant liability. Please try again, or contact support if it keeps happening.';
};
